import {
    Dispatch,
    ReactNode,
    SetStateAction,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react'
import { ImageInterface } from '../pages/Accounts/Register'
import {
    CreateAccountInterface,
    TwoFactorSetup,
    UsernameAlreadyInUseError,
    createAccount,
    getAccountById,
    getLoggedUser,
    listAccountRules,
    updateAccount,
} from '../services/account'
import { BranchInfo } from './branch_context'
import { useConfigs } from './configs_context'
import { useLoading } from './loading_context'
import { useSnack } from './snack_context'
import { useCoin } from './coin_context'
import { isLogged } from '../services/auth'
import { EmployeeInfo } from '../pages/Employee/Home'

export interface AccountRule {
    id: number
    name: string
    group: string
}

export interface AccountInfo {
    id?: number
    username: string
    name: string
    password: string
    cpassword: string
    rules: AccountRule[]
    isMaster: boolean
    isActive: boolean
    loginWithImage: boolean
    image: ImageInterface | null
    branch: BranchInfo | null
    permissions?: string[]
    isEditing?: boolean
    ignoreCaptcha?: boolean
    preferedNotePrefix?: string | null
    localPrinterHost?: string | null
    twoFactorEnabled?: boolean
    twoFactorEnabledSaved?: boolean
    twoFactorReset?: boolean
    employees?: EmployeeInfo[]
}

const createAccountInfoDefault: AccountInfo = {
    username: '',
    name: '',
    password: '',
    cpassword: '',
    rules: [],
    isMaster: false,
    isActive: true,
    loginWithImage: false,
    image: null,
    branch: null,
    isEditing: false,
    permissions: [],
    ignoreCaptcha: false,
    preferedNotePrefix: null,
    twoFactorEnabled: false,
    twoFactorEnabledSaved: false,
    twoFactorReset: false,
}

export interface CreateAccountErrorInterface {
    username: string
    name: string
    password: string
    cpassword: string
    branch: string
}

const createAccountErrorDefault: CreateAccountErrorInterface = {
    username: '',
    name: '',
    password: '',
    cpassword: '',
    branch: '',
}

export interface AccountContextInterface {
    accountInfo: AccountInfo | null
    setAccountInfo: Dispatch<SetStateAction<AccountInfo | null>>
    createAccountInfo: AccountInfo
    setCreateAccountInfo: Dispatch<SetStateAction<AccountInfo>>
    createAccountError: CreateAccountErrorInterface
    setCreateAccountError: Dispatch<SetStateAction<CreateAccountErrorInterface>>
    twoFactorSetup: TwoFactorSetup | null
    setTwoFactorSetup: Dispatch<SetStateAction<TwoFactorSetup | null>>
    validate(): boolean
    handleCreateNewAccount(): Promise<{
        account: AccountInfo | null
        twoFactorSetup: TwoFactorSetup | null
    }>
    accountRules: AccountRule[]
    setAccountRules: Dispatch<SetStateAction<AccountRule[]>>
    acountRuleGroups: string[]
    startNewAccount(basicRules: AccountRule[]): void
    editAccount(id: number, basicRules: AccountRule[]): Promise<void>
}

export const accountContextDefault: AccountContextInterface = {
    accountInfo: null,
    setAccountInfo: () => {},
    createAccountInfo: createAccountInfoDefault,
    setCreateAccountInfo: () => {},
    createAccountError: createAccountErrorDefault,
    setCreateAccountError: () => {},
    twoFactorSetup: null,
    setTwoFactorSetup: () => {},
    validate: () => false,
    handleCreateNewAccount: async () => {
        return { account: null, twoFactorSetup: null }
    },
    accountRules: [],
    setAccountRules: () => {},
    acountRuleGroups: [],
    startNewAccount: () => {},
    editAccount: async () => {},
}

const accountContext = createContext<AccountContextInterface>(
    accountContextDefault
)

export function useAccount() {
    return useContext(accountContext)
}

interface Props {
    children: ReactNode
}

export const AccountContextProvider = ({ children }: Props) => {
    const loading = useLoading()
    const snack = useSnack()
    const [accounts, setAccounts] = useState<AccountInfo[]>([])
    const [accountInfo, setAccountInfo] = useState<AccountInfo | null>(null)
    const [createAccountInfo, setCreateAccountInfo] = useState<AccountInfo>(
        createAccountInfoDefault
    )
    const [createAccountError, setCreateAccountError] =
        useState<CreateAccountErrorInterface>(createAccountErrorDefault)
    const [twoFactorSetup, setTwoFactorSetup] = useState<TwoFactorSetup | null>(
        null
    )
    const [accountRules, setAccountRules] = useState<AccountRule[]>([])
    const [acountRuleGroups, setAccountRuleGroups] = useState<string[]>([])
    const { configs } = useConfigs()
    const { coins } = useCoin()

    useEffect(() => {
        const newGroups: string[] = []
        accountRules.forEach((rule) => {
            if (newGroups.find((group) => group === rule.group)) return
            newGroups.push(rule.group)
        })
        setAccountRuleGroups(newGroups)
    }, [accountRules])

    const startNewAccount = useCallback((basicRules: AccountRule[]) => {
        setCreateAccountInfo({
            ...createAccountInfoDefault,
            rules: basicRules,
        })
        setCreateAccountError({ ...createAccountErrorDefault })
        setTwoFactorSetup(null)
    }, [])

    const editAccount = useCallback(
        async (id: number, basicRules: AccountRule[]) => {
            try {
                const account = await getAccountById(id, accountRules, coins)

                setCreateAccountInfo((prev) => ({
                    ...account,
                    isEditing: true,
                    twoFactorEnabledSaved: account.twoFactorEnabled,
                    rules: [
                        ...account.rules,
                        ...basicRules.filter(
                            (br) => !account.rules.find((r) => r.id === br.id)
                        ),
                    ],
                }))
                setCreateAccountError({ ...createAccountErrorDefault })
                setTwoFactorSetup(null)
            } catch (error) {
                console.log(error)
                snack.error('Conta não encontrada')
                throw new Error('Account not found')
            }
        },
        [accountRules, coins, snack]
    )

    const validate = useCallback((): boolean => {
        let isOk = true
        const newCreateAccountErrorInfo = { ...createAccountErrorDefault }

        if (!createAccountInfo.username) {
            isOk = false
            newCreateAccountErrorInfo.username = 'Campo obrigatório'
        }
        if (!createAccountInfo.name) {
            isOk = false
            newCreateAccountErrorInfo.name = 'Campo obrigatório'
        }
        if (!createAccountInfo.password && !createAccountInfo.isEditing) {
            isOk = false
            newCreateAccountErrorInfo.password = 'Campo obrigatório'
        }
        if (!createAccountInfo.cpassword && !createAccountInfo.isEditing) {
            isOk = false
            newCreateAccountErrorInfo.cpassword = 'Campo obrigatório'
        }
        if (
            createAccountInfo.password &&
            createAccountInfo.password.length < 6
        ) {
            isOk = false
            newCreateAccountErrorInfo.password =
                'Deve conter no mínimo 6 caracteres'
        }
        if (
            createAccountInfo.cpassword &&
            createAccountInfo.cpassword.length < 6
        ) {
            isOk = false
            newCreateAccountErrorInfo.cpassword =
                'Deve conter no mínimo 6 caracteres'
        }
        if (
            createAccountInfo.password.length >= 6 &&
            createAccountInfo.cpassword.length >= 6 &&
            createAccountInfo.password !== createAccountInfo.cpassword
        ) {
            isOk = false
            newCreateAccountErrorInfo.password = 'As senhas não coincidem'
            newCreateAccountErrorInfo.cpassword = 'As senhas não coincidem'
        }
        if (!createAccountInfo.name) {
            isOk = false
            newCreateAccountErrorInfo.name = 'Campo obrigatório'
        }
        /* if (
            accountInfo?.isMaster &&
            !createAccountInfo.isMaster &&
            configs.systemBranch === SystemBranchEnum.Branch &&
            !createAccountInfo.branch
        ) {
            isOk = false
            newCreateAccountErrorInfo.branch = 'Campo obrigatório'
        } */

        setCreateAccountError({ ...newCreateAccountErrorInfo })
        return isOk
    }, [createAccountInfo])

    const handleCreateNewAccount = useCallback(async (): Promise<{
        account: AccountInfo | null
        twoFactorSetup: TwoFactorSetup | null
    }> => {
        let account: AccountInfo | null = null
        let twoFactorSetup: TwoFactorSetup | null = null
        loading.show()
        try {
            const body: CreateAccountInterface = {
                username: createAccountInfo.username,
                senha: createAccountInfo.password || undefined,
                nome: createAccountInfo.name,
                flag_ativo: createAccountInfo.isActive ? 1 : 0,
                id_permissions: createAccountInfo.rules.map((r) => r.id),
                flag_login_face: createAccountInfo.loginWithImage ? 1 : 0,
                path_face: createAccountInfo.image?.content ?? null,
                id_filial_master: createAccountInfo.branch?.id,
                flag_ignorar_captcha: Number(createAccountInfo.ignoreCaptcha),
                prefixo_preferido: createAccountInfo.preferedNotePrefix || null,
                host_impressora_local:
                    createAccountInfo.localPrinterHost || null,
                flag_2fa: createAccountInfo.twoFactorEnabled ? 1 : 0,
            }
            if (!createAccountInfo.isEditing) {
                const result = await createAccount(body)
                snack.success('Conta criada com sucesso')
                account = { ...createAccountInfo, id: result.id_usuario }
                twoFactorSetup = result.twoFactor
            } else {
                twoFactorSetup = await updateAccount({
                    ...body,
                    id_usuario: createAccountInfo.id ?? 0,
                    reset_2fa: Boolean(createAccountInfo.twoFactorReset),
                })
                snack.success('Conta atualizada com sucesso')
                account = { ...createAccountInfo }
            }

            if (twoFactorSetup?.otpauth_url) setTwoFactorSetup(twoFactorSetup)
        } catch (error) {
            if (error instanceof UsernameAlreadyInUseError) {
                setCreateAccountError((prev) => ({
                    ...prev,
                    username: 'Usuário já está em uso',
                }))
            } else snack.connectionFail(error)
            window.scroll(0, 0)
        }
        loading.hide()
        return { account, twoFactorSetup }
    }, [createAccountInfo, loading, snack])

    const value = useMemo(
        () => ({
            accountInfo,
            setAccountInfo,
            createAccountInfo,
            setCreateAccountInfo,
            createAccountError,
            setCreateAccountError,
            twoFactorSetup,
            setTwoFactorSetup,
            validate,
            handleCreateNewAccount,
            accountRules,
            setAccountRules,
            acountRuleGroups,
            startNewAccount,
            editAccount,
        }),
        [
            accountInfo,
            createAccountInfo,
            createAccountError,
            twoFactorSetup,
            validate,
            handleCreateNewAccount,
            accountRules,
            acountRuleGroups,
            startNewAccount,
            editAccount,
        ]
    )

    return (
        <accountContext.Provider value={value}>
            {children}
        </accountContext.Provider>
    )
}
